/**
 * BlogArticle — /blog/article/:slug
 * ════════════════════════════════
 * Renders any article from HELP_ARTICLES, GUIDE_ARTICLES, or MENTOR_GUIDE_ARTICLES.
 * Same rich layout as TrainingArticle but with blog-specific sidebar CTAs.
 */
import React, { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useSEO, jsonLd } from '../utils/seo';
import {
  HELP_ARTICLES,
  GUIDE_ARTICLES,
  MENTOR_GUIDE_ARTICLES,
} from '../data/blogContent';
import './BlogArticle.css';

// Merge all article pools
const ALL_ARTICLES = [
  ...HELP_ARTICLES,
  ...GUIDE_ARTICLES,
  ...MENTOR_GUIDE_ARTICLES,
];

const TAG_MAP = {
  'Getting Started': { color: '#16a34a', bg: '#f0fdf4' },
  'Payments':        { color: '#2563eb', bg: '#eff6ff' },
  'Session Help':    { color: '#d97706', bg: '#fffbeb' },
  'Trust & Safety':  { color: '#7c3aed', bg: '#f5f3ff' },
  'Reviews':         { color: '#0891b2', bg: '#f0f9ff' },
  'For Mentors':     { color: '#dc2626', bg: '#fef2f2' },
  'Guide':           { color: '#374151', bg: '#f9fafb' },
};

function getTag(article) {
  if (HELP_ARTICLES.find(a => a.slug === article.slug)) {
    const t = HELP_ARTICLES.find(a => a.slug === article.slug);
    return t?.tag || 'Guide';
  }
  if (MENTOR_GUIDE_ARTICLES.find(a => a.slug === article.slug)) return 'For Mentors';
  return 'Guide';
}

// Related: other articles (excluding current), max 4
function getRelated(slug) {
  return ALL_ARTICLES.filter(a => a.slug !== slug).slice(0, 4);
}

export default function BlogArticle({ onSignIn }) {
  const { slug }   = useParams();
  const navigate   = useNavigate();
  const article    = ALL_ARTICLES.find(a => a.slug === slug);
  const related    = article ? getRelated(slug) : [];
  const tag        = article ? getTag(article) : 'Guide';
  const tagStyle   = TAG_MAP[tag] || TAG_MAP['Guide'];
  const isMentorArticle = MENTOR_GUIDE_ARTICLES.find(a => a.slug === slug);

  useSEO({
    title:       article?.title,
    description: article?.excerpt || article?.desc,
    type:        'article',
    canonical:   article ? `/blog/article/${article.slug}` : '/blog',
    jsonLd:      article ? jsonLd.breadcrumb([
      { name: 'Home',        path: '/' },
      { name: 'Help Centre', path: '/blog' },
      { name: article.title, path: `/blog/article/${article.slug}` },
    ]) : undefined,
  });

  useEffect(() => {
    if (!article) navigate('/blog', { replace: true });
  }, [article, navigate]);

  if (!article) return null;

  return (
    <>
      <Navbar onSignIn={onSignIn} />
      <div className="ba-page">
        <div className="ba-layout">

          {/* ── Article ──────────────────────────────────────── */}
          <article className="ba-main">

            {/* Breadcrumb */}
            <nav className="ba-breadcrumb">
              <Link to="/">Home</Link>
              <span>›</span>
              <Link to="/blog">Help Centre</Link>
              <span>›</span>
              <span style={{ color: tagStyle.color }}>{tag}</span>
            </nav>

            {/* Header */}
            <header className="ba-header">
              <span className="ba-tag" style={{ color: tagStyle.color, background: tagStyle.bg }}>
                {tag}
              </span>
              <h1 className="ba-title">{article.title}</h1>
              <p className="ba-excerpt">{article.excerpt || article.desc}</p>
              <div className="ba-meta">
                <span>✍️ DevBrains Team</span>
                <span>·</span>
                <span>📅 {new Date(article.publishedAt).toLocaleDateString('en-IN', { day:'2-digit', month:'long', year:'numeric' })}</span>
                {article.readTimeMins && <><span>·</span><span>⏱ {article.readTimeMins} min read</span></>}
              </div>
            </header>

            {/* Emoji cover */}
            <div className="ba-cover" style={{ background: `${tagStyle.color}10` }}>
              <span className="ba-cover-emoji">{article.emoji || '📄'}</span>
            </div>

            {/* Body */}
            <div className="ba-body" dangerouslySetInnerHTML={{ __html: article.body }} />

            {/* Share */}
            <div className="ba-share">
              <span className="ba-share-label">Share this article</span>
              <div className="ba-share-btns">
                {[
                  { label: 'LinkedIn', bg: '#0A66C2', url: `https://www.linkedin.com/sharing/share-offsite/?url=https://devbrains.in/blog/article/${article.slug}` },
                  { label: 'Twitter', bg: '#000', url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=https://devbrains.in/blog/article/${article.slug}` },
                  { label: 'WhatsApp', bg: '#25D366', url: `https://wa.me/?text=${encodeURIComponent(article.title + ' — https://devbrains.in/blog/article/' + article.slug)}` },
                ].map(s => (
                  <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer"
                    className="ba-share-btn" style={{ background: s.bg }}>{s.label}</a>
                ))}
              </div>
            </div>

            <Link to="/blog" className="ba-back">← Back to Help Centre</Link>
          </article>

          {/* ── Sidebar ──────────────────────────────────────── */}
          <aside className="ba-sidebar">

            {/* CTA — different for mentor vs mentee articles */}
            {isMentorArticle ? (
              <div className="ba-cta-card mentor-cta">
                <div className="ba-cta-icon">🧑‍🏫</div>
                <div className="ba-cta-headline">Ready to start mentoring?</div>
                <p className="ba-cta-sub">Join 2,400+ verified mentors. Earn ₹50k–₹1.5L per month. Applications reviewed in 5 days.</p>
                <Link to="/become-mentor" className="ba-cta-btn">Apply to Become a Mentor →</Link>
                <div className="ba-cta-trust">✅ Free to apply · 85% revenue share</div>
              </div>
            ) : (
              <div className="ba-cta-card">
                <div className="ba-cta-icon">🚀</div>
                <div className="ba-cta-headline">Ready to find your mentor?</div>
                <p className="ba-cta-sub">2,400+ verified engineers from Google, Shopee, Grab and more. First session free.</p>
                <div className="ba-cta-stats">
                  {[['4.9★', 'Avg Rating'], ['Free', 'First Session'], ['15 min', 'Avg response']].map(([n, l]) => (
                    <div key={l} className="ba-cta-stat"><strong>{n}</strong><span>{l}</span></div>
                  ))}
                </div>
                <Link to="/mentors" className="ba-cta-btn">Find My Mentor →</Link>
                <div className="ba-cta-trust">🔒 Razorpay secured · Money-back guaranteed</div>
              </div>
            )}

            {/* Related articles */}
            {related.length > 0 && (
              <div className="ba-related">
                <div className="ba-related-label">Related Articles</div>
                {related.map(a => {
                  const t = getTag(a);
                  const ts = TAG_MAP[t] || TAG_MAP['Guide'];
                  return (
                    <Link key={a.slug} to={`/blog/article/${a.slug}`} className="ba-related-item">
                      <span className="ba-related-emoji">{a.emoji || '📄'}</span>
                      <div>
                        <div className="ba-related-title">{a.title}</div>
                        <span className="ba-related-tag" style={{ color: ts.color }}>{t}</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Legal links */}
            <div className="ba-legal-links">
              <div className="ba-legal-label">Legal</div>
              <Link to="/terms" className="ba-legal-item">📋 Terms &amp; Conditions</Link>
              <Link to="/privacy" className="ba-legal-item">🔒 Privacy Policy</Link>
              <a href="mailto:support@devbrains.in" className="ba-legal-item">✉️ Contact Support</a>
            </div>

          </aside>
        </div>
      </div>
    </>
  );
}
